import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { merge, set } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import FormikPatchTouched from 'modules/shared/Forms/FormikPatchTouched';
import FormikFillFields from 'modules/shared/Forms/FormikFillFields';
import { DefaultRecordType } from 'rc-table/lib/interface';
import { Button, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import ModalWrapper from 'modules/shared/ModalWrapper';
import { Checkbox, Input, MaskInput, Select } from 'modules/shared/Forms';
import AddCircle from 'mdi-react/AddCircleIcon';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import AccessPointIcon from 'mdi-react/AdjustIcon';
import HighlightOff from 'mdi-react/HighlightOffIcon';
import YesNoModal from 'modules/shared/YesNoModal';
import Search from 'mdi-react/SearchIcon';
import { Visitor, VisitorPhone } from 'entities/Visitor';
import { logoutSelector } from 'modules/common/LogoutBtn/selectors';
import ErrorMsg from 'modules/shared/ErrorMsg';
import DateTimePickerEvo from 'modules/shared/BookingTable/components/DateTimePicker/DateTimePickerEvo';
import { DataGrid, DataGridColumn } from 'modules/shared/DataGrid';
import { useGridState } from 'modules/shared/DataGrid/hooks/useGridState';
import { RootState } from 'rootReducer';
import { PhoneService } from '../../../../entities/Phone/service';
// import { clientDateFormat } from '../../../../entities/Trip/constants';
import { commonTripSelector } from '../../../common/CommonTrip/selectors';
import { cleanError } from '../../../common/CommonTrip/actions';
import { useModal } from '../../../shared/hooks';
import classes from './VisitorsTab.module.scss';

import Loader from '../../../kiosk/components/Loader';
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  defaultGridState,
  defaultGridStatePage1000,
} from './services/constants';
import {
  getVisitorsRequest,
  getVisitorsError,
  getVisitorPhonesRequest,
  getVisitorPhoneDelete,
  getVisitorDelete,
  getVisitorsAddOrUpdate,
  getVisitorPhoneAdd,
  getVisitorPhoneLatest,
} from './services/actions';

export const VisitorsTabContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { branchTitle, branches } = useSelector(logoutSelector);
  const visitors = useSelector((state: RootState) => state.adiminModuleReducer.visitorsTabReducer.visitors);
  const error = useSelector((state: RootState) => state.adiminModuleReducer.visitorsTabReducer.error);
  const loading = useSelector((state: RootState) => state.adiminModuleReducer.visitorsTabReducer.loading);
  const phones = useSelector((state: RootState) => state.adiminModuleReducer.visitorPhonesTabReducer.phones);

  const { isShow: isShowConfirmDelete, openModal: openConfirmDelete, closeModal: closeConfirmDelete } = useModal();

  const { isShow: isShowVisitorForm, openModal: openVisitorForm, closeModal: closeVisitorForm } = useModal();
  const { isShow: isShowPhoneForm, openModal: openPhoneForm, closeModal: closePhoneForm } = useModal();

  const { errorTrip } = useSelector(commonTripSelector);

  const { isShow: isShowErr, openModal: openErrToast, closeModal: closeErrToast } = useModal();

  const [visitorSelected, setVisitorSelected] = useState<Visitor | null>(null);
  const [visitorEdited, setVisitorEdited] = useState<Visitor | null>(null);
  const usePreparedVisitor = (values: Visitor | null) =>
    useMemo(() => {
      const data: any = merge({}, values);
      set(data, 'lastName', values?.lastName);
      set(data, 'firstName', values?.firstName);
      set(data, 'middleName', values?.middleName);
      set(data, 'branchId', values?.branchId ?? branchTitle.id);
      set(data, 'visitorRole', values?.visitorRole ?? 1);
      return data;
    }, [values]);
  const initialVisitorFormValues = usePreparedVisitor(visitorEdited);
  const visitorValidation = Yup.object().shape({
    lastName: Yup.string()
      .trim()
      .nullable()
      .matches(/^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/, 'Поле "Прізвище" повинно містити тільки літери')
      .required('Поле "Прізвище" обов\'язкове'),
    firstName: Yup.string()
      .trim()
      .nullable()
      .matches(/^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/, 'Поле "Ім\'я" повинно містити тільки літери')
      .required('Поле "Ім\'я" обов\'язкове'),
    middleName: Yup.string()
      .trim()
      .nullable()
      .matches(/^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/, 'Поле "По батькові" повинно містити тільки літери')
      .required('Поле "По батькові" обов\'язкове'),
    branchId: Yup.string().trim().nullable().required('Поле "Підрозділ" обов\'язкове'),
    visitorRole: Yup.string().trim().nullable().required('Поле "Роль" обов\'язкове'),
  });

  const [phoneSelected, setPhoneSelected] = useState<VisitorPhone | null>(null);
  const [phoneEdited, setPhoneEdited] = useState<VisitorPhone | null>(null);
  const usePreparedPhone = (values: VisitorPhone | null) =>
    useMemo(() => {
      const data: any = merge({}, values);
      set(data, 'number', values?.number);
      set(data, 'id', values?.id);
      return data;
    }, [values]);
  const initialPhoneFormValues = usePreparedPhone(phoneEdited);
  const visitorPhoneValidation = Yup.object().shape({
    number: Yup.string().trim().nullable().required('Поле "Телефон" обов\'язкове'),
  });

  const [filterFIOValue, setFilterFIOValue] = useState('');
  const [filterNumberValue, setFilterNumberValue] = useState('');

  const handleFilterFIOValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterFIOValue(e.target.value);
  };
  const handleFilterNumberValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterNumberValue(e.target.value);
  };

  const handleClearFIOIconClick = () => {
    setFilterFIOValue('');
  };
  const handleClearNumberIconClick = () => {
    setFilterNumberValue('');
  };

  const handleSearchBtnClick = () => {
    setVisitorSelected(null);
    setPhoneSelected(null);
    dispatch(getVisitorsRequest(filterFIOValue, filterNumberValue));
  };

  const handleDeleteNumberBtnClick = () => {
    if (phoneSelected && visitorSelected) {
      dispatch(getVisitorPhoneDelete(phoneSelected.id, visitorSelected.id));
    }
  };

  const handleLatestNumberBtnClick = () => {
    if (phoneSelected && visitorSelected) {
      dispatch(getVisitorPhoneLatest(phoneSelected.id, visitorSelected.id));
    }
  };

  const gridState = useGridState(visitors, defaultGridState, () => {
    setVisitorSelected(null);
    dispatch(getVisitorPhonesRequest(0));
  });
  const phonesGridState = useGridState(phones, defaultGridStatePage1000, () => {
    setPhoneSelected(null);
  });

  const handleCreateVisitorBtnClick = () => {
    setVisitorEdited(null);
    openVisitorForm();
  };

  const handleAddNumberBtnClick = () => {
    setPhoneEdited({ visitorId: visitorSelected?.id } as VisitorPhone);
    openPhoneForm();
  };

  useEffect(() => {
    dispatch(getVisitorsRequest(filterFIOValue, filterNumberValue));
    gridState.setFirstPage();
    phonesGridState.setFirstPage();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      dispatch(getVisitorsRequest(filterFIOValue, filterNumberValue));
      if (visitorSelected) {
        dispatch(getVisitorPhonesRequest(visitorSelected.id));
      }
    }, 500);
  }, [branchTitle]);

  useEffect(() => {
    const updateData = setInterval(() => dispatch(getVisitorsRequest(filterFIOValue, filterNumberValue)), 50000);

    if (error) clearInterval(updateData);
    return function cleanup() {
      clearInterval(updateData);
    };
  });

  useEffect(() => {
    if (errorTrip) {
      return openErrToast();
    }
    closeErrToast();
  }, [errorTrip]);

  const addRowColorByStatus = (rowData: DefaultRecordType) => {
    const item = rowData;
    return {
      onDoubleClick: () => {
        setVisitorEdited(item as Visitor);
        openVisitorForm();
      },
      onClick: () => {
        setVisitorSelected(item as Visitor);
        phonesGridState.setFirstPage();
        setPhoneSelected(null);
        dispatch(getVisitorPhonesRequest(item.id));
      },
      style: {
        backgroundColor: item.id === visitorSelected?.id ? '#add8e6' : '',
      },
    };
  };

  const addPhonesRowColorByStatus = (rowData: DefaultRecordType) => {
    const item = rowData;
    return {
      onDoubleClick: () => {
        setPhoneEdited(item as VisitorPhone);
        openPhoneForm();
      },
      onClick: () => {
        setPhoneSelected(item as VisitorPhone);
      },
      style: {
        backgroundColor: item.id === phoneSelected?.id ? '#add8e6' : '',
      },
    };
  };

  return (
    <React.Fragment>
      <Container fluid className={classes.filterContainer}>
        <Row>
          <Col className="d-flex align-items-center">
            <Button variant="light" className="mr-2" onClick={() => handleCreateVisitorBtnClick()}>
              <AddCircle className={classes.plusIcon} /> Створити відвідувача
            </Button>

            <Button variant="light" className="mr-2" onClick={() => openConfirmDelete()}>
              <DeleteIcon className={classes.dangerIcon} /> Видалити відвідувача
            </Button>

            <Button
              variant="light"
              className="mr-2"
              onClick={() => handleAddNumberBtnClick()}
              disabled={!visitorSelected}
            >
              <AddCircle className={classes.plusIcon} /> Додати номер
            </Button>

            <Button
              variant="light"
              className="mr-2"
              onClick={() => handleLatestNumberBtnClick()}
              disabled={!phoneSelected}
            >
              <AccessPointIcon className={classes.plusIcon} />
              Встановити номер поточним
            </Button>

            <Button
              variant="light"
              className="mr-2"
              onClick={() => handleDeleteNumberBtnClick()}
              disabled={!phoneSelected}
            >
              <DeleteIcon className={classes.dangerIcon} />
              Видалити номер
            </Button>

            <InputGroup className={classes.filter}>
              <Form.Control
                placeholder="знайти ПІБ"
                as="input"
                type="text"
                name="filterName"
                onChange={handleFilterFIOValueChange}
                value={filterFIOValue}
              />
              <Search size={18} className={classes.searchIcon} onClick={() => handleSearchBtnClick()} />
              {!!filterFIOValue && (
                <HighlightOff size={18} className={classes.clearFilterIcon} onClick={handleClearFIOIconClick} />
              )}
            </InputGroup>

            <InputGroup className={classes.filter}>
              <Form.Control
                placeholder="знайти номер"
                as="input"
                type="text"
                name="filterPhone"
                onChange={handleFilterNumberValueChange}
                value={filterNumberValue}
              />
              <Search size={18} className={classes.searchIcon} onClick={() => handleSearchBtnClick()} />
              {!!filterNumberValue && (
                <HighlightOff size={18} className={classes.clearFilterIcon} onClick={handleClearNumberIconClick} />
              )}
            </InputGroup>
            <Button variant="light" className="mr-2" onClick={() => handleSearchBtnClick()}>
              Пошук
            </Button>

            {!!visitors.length && <span className="ml-2">Всього: {visitors.length}</span>}
            {!!phones.length && <span className="ml-0">, номерів відвідувача: {phones.length}</span>}
          </Col>
        </Row>
      </Container>

      {loading && <Loader />}
      {gridState.gridData ? (
        <Row className={classes.container}>
          <Col>
            <DataGrid
              pageable
              data={gridState.gridData}
              sort={gridState.sort}
              total={gridState.total}
              skip={gridState.pagination?.skip}
              pageSize={gridState.pagination?.pageSize}
              onPageChange={gridState.handlePageChange}
              onSortChange={gridState.handleSortChange}
              onRow={addRowColorByStatus}
            >
              <DataGridColumn title="ПІБ" dataIndex="fullName" key="fullName" width="25%" />
              <DataGridColumn
                title="Наявність FaceID"
                dataIndex="isFaceId"
                key="isFaceId"
                render={(v) => <React.Fragment>{v ? 'Да' : ''}</React.Fragment>}
                width="5%"
              />
              <DataGridColumn title="Роль" dataIndex="visitorRoleStr" key="visitorRoleStr" width="5%" />
              <DataGridColumn title="Дата оновлення" dataIndex="updatedDateStr" key="updatedDateStr" width="5%" />
              <DataGridColumn title="Дата створення" dataIndex="createdDateStr" key="createdDateStr" width="5%" />
              <DataGridColumn title="ІД" dataIndex="id" key="id" width="5%" />
            </DataGrid>
          </Col>
          <Col className="d-flex justify-content-left">
            <Row>
              <Col>
                <DataGrid
                  pageable
                  data={phonesGridState.gridData}
                  total={phonesGridState.total}
                  skip={phonesGridState.pagination?.skip}
                  pageSize={phonesGridState.pagination?.pageSize}
                  onPageChange={phonesGridState.handlePageChange}
                  onRow={addPhonesRowColorByStatus}
                >
                  <DataGridColumn title="Номер телефону" dataIndex="number" key="number" />
                  <DataGridColumn title="Дата створенння" dataIndex="createdDateStr" key="createdDate" />
                  <DataGridColumn title="ІД" dataIndex="id" key="id" />
                </DataGrid>
              </Col>
            </Row>
          </Col>
        </Row>
      ) : null}
      <ErrorMsg
        isShow={!!error}
        error={error?.message || 'Помилка при отриманні данних'}
        closeHandler={() => dispatch(getVisitorsError(null))}
      />
      <ErrorMsg
        isShow={isShowErr}
        closeHandler={() => {
          closeErrToast();
          dispatch(cleanError());
        }}
        error={errorTrip?.data?.Errors[0]?.Message || 'Виникла помилка!'}
      />

      <YesNoModal
        isShow={isShowConfirmDelete}
        title="Буде видалено відвідувача та його номери телефонів. Ви впевнені?"
        acceptHandler={() => {
          closeConfirmDelete();
          if (visitorSelected) dispatch(getVisitorDelete(visitorSelected.id, filterFIOValue, filterNumberValue));
        }}
        closeModal={closeConfirmDelete}
      />

      <ModalWrapper show={isShowVisitorForm} onHide={closeVisitorForm}>
        <Formik
          initialValues={initialVisitorFormValues}
          validationSchema={visitorValidation}
          validateOnMount
          onSubmit={() => {}}
        >
          {({ values, isValid, handleSubmit }) => {
            const onSubmitClick = () => {
              if (isValid) {
                closeVisitorForm();
                handleSubmit(values);
                // @ts-ignore
                values.visitorRole = values.visitorRole ? Number(values.visitorRole) : 1;
                // @ts-ignore
                values.createdDate = new Date(values.createdDate);
                // @ts-ignore
                values.updatedDate = new Date(values.updatedDate);
                // @ts-ignore
                values.isFaceId = values.isFaceId ? true : false;
                dispatch(getVisitorsAddOrUpdate(values, filterFIOValue, filterNumberValue));
              }
            };
            return (
              <>
                <h4 className="text-center mb-3">
                  {values?.id ? 'Редагування даних відвідувача' : 'Створення нового відвідувача'}
                </h4>
                <form onSubmit={handleSubmit}>
                  <FormikPatchTouched />
                  <FormikFillFields />
                  <Select name="branchId" label="Підрозділ" onChange={() => {}} className="customSelect mb-2">
                    {branches
                      ?.filter((b) => !b.isDeleted)
                      ?.map((b) => (
                        <option value={b.id}>{b.name}</option>
                      ))}
                  </Select>
                  <Select name="visitorRole" label="Роль відвідувача" onChange={() => {}} className="customSelect mb-2">
                    <option value={1}>Водій</option>
                    <option value={2}>Експедитор</option>
                  </Select>
                  <Input name="lastName" label="Прізвище" maxLength={40} onChange={() => {}} />
                  <Input name="firstName" label="Ім'я" maxLength={40} onChange={() => {}} />
                  <Input name="middleName" label="По батькові" maxLength={40} onChange={() => {}} />
                  <Checkbox name="isFaceId" label="Наявність FaceID" onChange={() => {}} />
                  <DateTimePickerEvo label="Дата оновлення" name="updatedDate" />
                  <DateTimePickerEvo label="Дата створення" name="createdDate" />
                  <div className="d-flex justify-content-around mt-5">
                    <Button className="mr-4 px-5" onClick={closeVisitorForm} variant="outline-secondary">
                      Скасувати
                    </Button>
                    <Button className="px-5" onClick={onSubmitClick} variant="outline-primary">
                      Зберегти
                    </Button>
                  </div>
                </form>
              </>
            );
          }}
        </Formik>
      </ModalWrapper>

      <ModalWrapper show={isShowPhoneForm} onHide={closePhoneForm}>
        <Formik
          initialValues={initialPhoneFormValues}
          validationSchema={visitorPhoneValidation}
          validateOnMount
          onSubmit={() => {}}
        >
          {({ values, isValid, handleSubmit }) => {
            const onSubmitClick = () => {
              if (isValid) {
                closePhoneForm();
                handleSubmit(values);
                values.number = PhoneService.unformatPhone(values.number);
                values.createdDate = new Date(values.createdDate);
                dispatch(getVisitorPhoneAdd(values.visitorId, values));
                console.log(values);
              }
            };
            return (
              <>
                <h4 className="text-center mt-3">{`${visitorSelected?.lastName} ${visitorSelected?.firstName ?? ''} ${
                  visitorSelected?.middleName ?? ''
                }`}</h4>
                <h5 className="text-center mb-2">{values.id ? 'Редагувати телефон' : 'Додати телефон'}</h5>
                <form onSubmit={handleSubmit}>
                  <MaskInput
                    name="number"
                    label="Телефон"
                    placeholder="38 (0XX) XXX-XX-XX"
                    onChange={() => {}}
                    value={values.number}
                  />
                  <DateTimePickerEvo label="Дата створення" name="createdDate" />
                  <div className="d-flex justify-content-around mt-5">
                    <Button className="mr-4 px-5" onClick={closePhoneForm} variant="outline-secondary">
                      Скасувати
                    </Button>
                    <Button className="px-5" onClick={onSubmitClick} variant="outline-primary">
                      Зберегти
                    </Button>
                  </div>
                </form>
              </>
            );
          }}
        </Formik>
      </ModalWrapper>
    </React.Fragment>
  );
};
